import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  getPreferenceByKey,
  addOrUpdatePreference,
  PreferenceData,
} from "../services/PreferenceServices";
import { useAuth } from "./AuthProvider";

// Define the context type
interface ThemeContextType {
  currentTheme: string;
  setTheme: (theme: string) => void;
  getUserTheme: () => void;
}

// Create the Theme Context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme Provider Component
export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentTheme, setCurrentTheme] = useState("defaultMode");
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      getUserTheme();
    } else {
      setCurrentTheme("defaultMode");
    }
  }, [user]); // Only run once, when the component mounts

  const getUserTheme = async () => {
    try {
      const response = await getPreferenceByKey("theme");
      setCurrentTheme(response.preference_value);
    } catch (error) {
      console.error("Error fetching theme preference:", error);
    }
  };

  const setTheme = (theme: string) => {
    // setTheme("defaultMode");
    const preferenceData: Omit<
      PreferenceData,
      "created_at" | "updated_at" | "user_id"
    > = {
      preference_key: "theme",
      preference_value: theme,
    };

    addOrUpdatePreference(preferenceData);

    setCurrentTheme(theme);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, getUserTheme }}>
      <div className={currentTheme}>{children}</div>
    </ThemeContext.Provider>
  );
};

// Custom Hook
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
